'use client'

import { useContext, useLayoutEffect, useRef, useState } from 'react'
import { Cell, Divider, Spacer, Text } from '@vinted/web-ui'

import { LinkifiedMessage } from '@marketplace-web/linkified-message/linkified-message-feature'
import { TranslationControl } from '@marketplace-web/i18n/translations-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { Translation } from '@marketplace-web/i18n/i18n-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  clickEvent,
  PluginsContext,
  ItemPageDescriptionPluginModel,
} from '@marketplace-web/item-page/item-page-data'

const LINE_HEIGHT = 22

const ItemPageDescriptionPlugin = ({
  itemId,
  description,
  localization,
  isLocalized,
  maxLines,
}: ItemPageDescriptionPluginModel) => {
  const translate = useTranslate()
  const { track } = useTracking()

  const { updatePlugins } = useContext(PluginsContext)

  const contentRef = useRef<HTMLDivElement>(null)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [showMore, setShowMore] = useState(false)

  useLayoutEffect(() => {
    const element = contentRef.current

    if (element && element.scrollHeight > element.offsetHeight) {
      setIsCollapsed(true)
      setShowMore(true)
    } else {
      setIsCollapsed(false)
    }
  }, [contentRef, description])

  const handleShowMore = () => {
    setIsCollapsed(false)
    setShowMore(false)

    track(
      clickEvent({
        screen: 'item',
        target: 'read_more',
      }),
    )
  }

  const handleDynamicTranslate = async (isTranslated: boolean) => {
    if (isTranslated) {
      await updatePlugins?.('translatable', { localize: false })
    } else {
      await updatePlugins?.('translatable', { localize: true })
    }
  }

  const descriptionStyle = isCollapsed
    ? { maxHeight: `${LINE_HEIGHT * maxLines}px`, overflow: 'hidden' }
    : undefined

  return (
    <>
      <Divider />
      <Spacer />
      <Cell styling="tight">
        <div className="u-text-wrap" itemProp="description">
          <div style={descriptionStyle} ref={contentRef}>
            <Text as="span" format>
              <LinkifiedMessage
                text={description}
                trackingData={{
                  item_id: itemId.toString(),
                }}
              />
            </Text>
          </div>
          {showMore && (
            <button type="button" onClick={handleShowMore}>
              <Text
                as="span"
                clickable
                text={translate('item.description.actions.show_more')}
                type="body"
              />
            </button>
          )}
        </div>
        <Spacer />
        {localization !== Translation.None && (
          <>
            <TranslationControl
              isTranslated={isLocalized}
              autotranslate={localization === Translation.Auto}
              onClick={handleDynamicTranslate}
              tracking={{
                target: 'translate_description',
                targetDetails: JSON.stringify({
                  item_id: itemId,
                }),
              }}
            />
            <Spacer />
          </>
        )}
      </Cell>
    </>
  )
}

export default ItemPageDescriptionPlugin
